import React, { useState, useEffect } from 'react';
import { STRRoom, STRBooking } from '../types';
import { formatNumber, parseNumberInput, formatDate } from '../utils';
import { useResponsive } from '../../hooks/useResponsive';

interface Props {
  room: STRRoom;
  booking: STRBooking | null; // null = create
  isExtension: boolean;
  selStart: Date | null;
  selEnd: Date | null;
  onSave: (data: any, isEdit: boolean) => void;
  onClose: () => void;
}

export default function BookingModal({ room, booking, isExtension, selStart, selEnd, onSave, onClose }: Props) {
  const { isMobile } = useResponsive();
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [channel, setChannel] = useState('삼삼엠투');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [negotiatedPrice, setNegotiatedPrice] = useState('');
  const [weeklyRent, setWeeklyRent] = useState(formatNumber(room.weekly_rent || 0));
  const [weeklyMaint, setWeeklyMaint] = useState(formatNumber(room.weekly_maintenance || 0));
  const [cleaningFee, setCleaningFee] = useState(formatNumber(room.weekly_cleaning || 0));
  const [cleaningExpense, setCleaningExpense] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [manualRent, setManualRent] = useState('0');
  const [manualMaint, setManualMaint] = useState('0');
  const [manualClean, setManualClean] = useState('0');
  const [manualFee, setManualFee] = useState('0');
  const [manualDiscount, setManualDiscount] = useState('0');

  const isEdit = !!booking;

  const fmtSet = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, '');
    const num = parseInt(digits, 10) || 0;
    setter(num === 0 ? '0' : formatNumber(num));
  };
  const fmtVal = (v: number) => (v === 0 ? '0' : formatNumber(v));

  useEffect(() => {
    if (booking) {
      setGuestName(booking.guest_name);
      setGuestPhone(booking.guest_phone);
      setChannel(booking.channel);
      setCheckIn(booking.check_in);
      setCheckOut(booking.check_out);
      setNegotiatedPrice(booking.negotiated_price ? fmtVal(booking.negotiated_price) : '');
      setWeeklyRent(fmtVal(booking.booking_weekly_rent || room.weekly_rent || 0));
      setWeeklyMaint(fmtVal(booking.booking_weekly_maint || room.weekly_maintenance || 0));
      setCleaningFee(fmtVal(booking.booking_weekly_clean || room.weekly_cleaning || 0));
      setCleaningExpense(booking.cleaning_expense ? fmtVal(booking.cleaning_expense) : '');
    } else {
      setCheckIn(selStart ? formatDate(selStart) : '');
      setCheckOut(selEnd ? formatDate(selEnd) : '');
      setChannel('삼삼엠투');
    }
  }, [booking, selStart, selEnd, isExtension, room]);

  // Calc
  let days = 0;
  if (checkIn && checkOut) {
    const d1 = new Date(checkIn + 'T00:00:00');
    const d2 = new Date(checkOut + 'T00:00:00');
    days = Math.round((d2.getTime() - d1.getTime()) / 86400000) + 1;
  }

  const wr = parseNumberInput(weeklyRent);
  const wm = parseNumberInput(weeklyMaint);
  const wc = parseNumberInput(cleaningFee);
  const autoRent = days > 0 ? Math.round(wr / 7 * days) : 0;
  const autoMaint = days > 0 ? Math.round(wm / 7 * days) : 0;
  const autoClean = wc;
  const autoSubtotal = autoRent + autoMaint + autoClean;
  const autoFee = Math.round(autoSubtotal * 0.033);
  const autoTotal = autoSubtotal - autoFee;
  const deposit = room.deposit || 330000;

  // 1주 금액 기반 자동계산값을 항상 합계에 반영
  const displayRent = autoRent;
  const displayMaint = autoMaint;
  const displayClean = autoClean;
  const displayDiscount = isEdit && booking ? (booking.discount || 0) : 0;
  const displayFee = isEdit && booking ? (booking.platform_fee ?? autoFee) : autoFee;
  const displaySubtotal = displayRent + displayMaint + displayClean;
  const displayAfterDiscount = displaySubtotal - displayDiscount;
  const displayTotal = displayAfterDiscount - displayFee;

  const handleSave = () => {
    const body: any = {
      guest_name: guestName,
      guest_phone: guestPhone,
      channel,
      booking_type: '일반',
      check_in: checkIn,
      check_out: checkOut,
      negotiated_price: parseNumberInput(negotiatedPrice),
      booking_weekly_rent: parseNumberInput(weeklyRent),
      booking_weekly_maint: parseNumberInput(weeklyMaint),
      booking_weekly_clean: parseNumberInput(cleaningFee),
      cleaning_expense: parseNumberInput(cleaningExpense),
    };

    if (manualMode) {
      const mR = parseNumberInput(manualRent);
      const mM = parseNumberInput(manualMaint);
      const mC = parseNumberInput(manualClean);
      const mD = parseNumberInput(manualDiscount);
      const mFeeBase = mR + mM + mC - mD;
      body.manual_rent = mR;
      body.manual_maint = mM;
      body.manual_clean = mC;
      body.discount = mD;
      body.platform_fee = parseNumberInput(manualFee);
    } else if (isEdit && booking) {
      // 편집 시: 1주 금액 기반 자동계산값 사용, 수수료/할인은 저장된 값 유지
      body.manual_rent = autoRent;
      body.manual_maint = autoMaint;
      body.manual_clean = autoClean;
      body.discount = booking.discount || 0;
      body.platform_fee = booking.platform_fee ?? autoFee;
    } else {
      body.manual_rent = autoRent;
      body.manual_maint = autoMaint;
      body.manual_clean = autoClean;
      body.discount = 0;
    }

    onSave(body, isEdit);
  };

  const openManualMode = () => {
    setManualMode(true);
    if (booking) {
      setManualRent(fmtVal(booking.total_rent || 0));
      setManualMaint(fmtVal(booking.total_maintenance || 0));
      setManualClean(fmtVal(booking.total_cleaning || 0));
      setManualDiscount(fmtVal(booking.discount || 0));
      setManualFee(fmtVal(booking.platform_fee || 0));
    } else {
      setManualRent(fmtVal(autoRent));
      setManualMaint(fmtVal(autoMaint));
      setManualClean(fmtVal(autoClean));
      setManualDiscount('0');
      setManualFee(fmtVal(autoFee));
    }
  };

  // 수동 모드 계산: 플랫폼 수수료 = (임대료+관리비+청소비-할인금액) * 3.3%
  const mRentVal = parseNumberInput(manualRent);
  const mMaintVal = parseNumberInput(manualMaint);
  const mCleanVal = parseNumberInput(manualClean);
  const mDiscountVal = parseNumberInput(manualDiscount);
  const mFeeBaseVal = mRentVal + mMaintVal + mCleanVal - mDiscountVal;
  const mCalcFee = Math.round(mFeeBaseVal * 0.033);
  const mFeeVal = parseNumberInput(manualFee);
  const mSubtotal = mRentVal + mMaintVal + mCleanVal;
  const mAfterDiscount = mSubtotal - mDiscountVal;
  const manualTotal = mAfterDiscount - mFeeVal;

  // c1 st-form styles
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', border: '1px solid #e2e5e9',
    borderRadius: 8, fontSize: isMobile ? 16 : 14, boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: isMobile ? 12 : 13, fontWeight: 500, color: '#888', marginBottom: 4,
  };
  const groupStyle: React.CSSProperties = { marginBottom: isMobile ? 10 : 14 };
  const rowStyle: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12,
  };
  const hintStyle: React.CSSProperties = {
    fontSize: 11, color: '#bbb', fontWeight: 400,
  };

  // Price summary row
  const summaryRow = (label: string, value: string, opts?: { bold?: boolean; color?: string; border?: boolean }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      ...(opts?.bold ? { fontWeight: 600 } : {}),
      ...(opts?.color ? { color: opts.color } : {}),
      ...(opts?.border ? { borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 8 } : { marginTop: 2 }),
    }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );

  // Manual input row
  const manualInputRow = (label: string, value: string, setter: (v: string) => void, opts?: { color?: string; minWidth?: number }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4, ...(opts?.color ? { color: opts.color } : {}) }}>
      <span style={{ minWidth: opts?.minWidth || 70 }}>{label}</span>
      <input
        style={{ flex: 1, textAlign: 'right', padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13 }}
        value={value} onChange={fmtSet(setter)}
      />
    </div>
  );

  return (
    <div
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{ background: '#fff', borderRadius: isMobile ? 10 : 12, padding: isMobile ? 16 : 24, width: isMobile ? 'calc(100vw - 24px)' : '90%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: isMobile ? '0 0 14px' : '0 0 20px' }}>
          <h3 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 600, margin: 0 }}>
            {isEdit ? '예약 수정' : '예약 추가'}
          </h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#aaa', padding: '4px', lineHeight: 1 }}
            aria-label="닫기"
          >
            ✕
          </button>
        </div>

        {/* 게스트 이름 */}
        <div style={groupStyle}>
          <label style={labelStyle}>게스트 이름</label>
          <input style={inputStyle} value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="홍길동" />
        </div>

        {/* 연락처 / 채널 */}
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>게스트 연락처</label>
            <input style={inputStyle} value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} placeholder="010-1234-5678" />
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>예약 채널</label>
            <select style={inputStyle} value={channel} onChange={(e) => setChannel(e.target.value)}>
              {['삼삼엠투', '리브애니웨어', '엔코스테이', '자리톡', '직방', '에어비앤비', '기타'].map((ch) => (
                <option key={ch} value={ch}>{ch}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 체크인 / 체크아웃 */}
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>체크인</label>
            <input type="date" style={inputStyle} value={checkIn} onChange={(e) => setCheckIn(e.target.value)} readOnly={!isEdit} />
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>체크아웃</label>
            <input type="date" style={inputStyle} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} readOnly={!isEdit} />
          </div>
        </div>

        {/* 1주 금액 섹션 */}
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>1주 금액</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: isMobile ? 6 : 8 }}>
            <div>
              <label style={labelStyle}>임대료</label>
              <input style={inputStyle} value={weeklyRent} onChange={fmtSet(setWeeklyRent)} />
            </div>
            <div>
              <label style={labelStyle}>관리비</label>
              <input style={inputStyle} value={weeklyMaint} onChange={fmtSet(setWeeklyMaint)} />
            </div>
            <div>
              <label style={labelStyle}>청소비(1회)</label>
              <input style={inputStyle} value={cleaningFee} onChange={fmtSet(setCleaningFee)} />
            </div>
          </div>
        </div>

        {/* 청소비 실 지출 */}
        <div style={{ ...groupStyle, marginTop: 8 }}>
          <label style={labelStyle}>
            청소비 실 지출 <span style={hintStyle}>실제 청소 비용 (지출)</span>
          </label>
          <input style={inputStyle} value={cleaningExpense} onChange={fmtSet(setCleaningExpense)} placeholder="미입력 시 표기 안함" />
        </div>

        {/* Price summary (c1: #f5f5f5, borderRadius 8, padding 12, fontSize 13) */}
        {!manualMode ? (
          <div style={{ marginTop: 12, padding: isMobile ? 10 : 12, background: '#f5f5f5', borderRadius: 8, fontSize: 13 }}>
            {summaryRow('기간', `${days}일`)}
            {summaryRow('임대료', `${formatNumber(displayRent)}원`)}
            {summaryRow('관리비', `${formatNumber(displayMaint)}원`)}
            {summaryRow('청소비(1회)', `${formatNumber(displayClean)}원`)}
            {summaryRow('합계 금액', `${formatNumber(displaySubtotal)}원`, { bold: true, border: true })}
            {displayDiscount > 0 && summaryRow('할인 금액', `-${formatNumber(displayDiscount)}원`, { color: '#e65100' })}
            {displayDiscount > 0 && summaryRow('할인 적용 금액', `${formatNumber(displayAfterDiscount)}원`, { bold: true })}
            {summaryRow(displayFee > 0 ? `플랫폼 수수료(${((displayFee / displaySubtotal) * 100).toFixed(1)}%)` : '플랫폼 수수료', displayFee > 0 ? `-${formatNumber(displayFee)}원` : '0원', { color: '#e65100' })}
            {summaryRow('정산 금액', `${formatNumber(displayTotal)}원`, { bold: true, border: true })}
            <div style={{ marginTop: 6, fontSize: 12, color: '#e65100' }}>+ 보증금 {formatNumber(deposit)}원 (별도)</div>
            <div style={{ textAlign: 'right', marginTop: 6 }}>
              <button type="button" onClick={openManualMode} style={{ fontSize: 12, color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'none' }}>
                금액 수정 ▸
              </button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 12, padding: isMobile ? 10 : 12, background: '#f5f5f5', borderRadius: 8, fontSize: 13 }}>
            {summaryRow('기간', `${days}일`)}
            {manualInputRow('임대료', manualRent, setManualRent)}
            {manualInputRow('관리비', manualMaint, setManualMaint)}
            {manualInputRow('청소비(1회)', manualClean, setManualClean)}
            {summaryRow('합계 금액', `${formatNumber(mSubtotal)}원`, { bold: true, border: true })}
            {manualInputRow('할인 금액', manualDiscount, setManualDiscount, { color: '#e65100' })}
            {mDiscountVal > 0 && summaryRow('할인 적용 금액', `${formatNumber(mAfterDiscount)}원`, { bold: true })}
            {manualInputRow('플랫폼 수수료', manualFee, setManualFee, { color: '#e65100', minWidth: 100 })}
            {summaryRow('정산 금액', `${formatNumber(manualTotal)}원`, { bold: true, border: true })}
            <div style={{ marginTop: 6, fontSize: 12, color: '#e65100' }}>+ 보증금 {formatNumber(deposit)}원 (별도)</div>
            <div style={{ textAlign: 'right', marginTop: 6 }}>
              <button type="button" onClick={() => setManualMode(false)} style={{ fontSize: 12, color: '#888', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'none' }}>
                ◂ 자동계산으로 복원
              </button>
            </div>
          </div>
        )}

        {/* 협의 금액 */}
        <div style={{ ...groupStyle, marginTop: 12 }}>
          <label style={labelStyle}>
            협의 금액 <span style={hintStyle}>입력 시 합계 대신 적용됩니다</span>
          </label>
          <input style={{ ...inputStyle, fontWeight: 600 }} value={negotiatedPrice} onChange={fmtSet(setNegotiatedPrice)} placeholder="미입력 시 자동계산 합계 적용" />
        </div>

        {/* 버튼 (c1: st-form-actions) */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: isMobile ? 10 : 8, marginTop: 20 }}>
          {isMobile ? (
            <>
              <button
                onClick={handleSave}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '100%', padding: '12px', border: 'none', borderRadius: 8,
                  background: '#1976d2', color: '#fff', fontSize: 14, fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {isEdit ? '예약 수정' : '예약 확정'}
              </button>
              <button
                onClick={onClose}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '100%', padding: '12px', border: '1px solid #e2e5e9', borderRadius: 8,
                  background: '#f5f5f5', color: '#333', fontSize: 14, fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                취소
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '8px 16px', border: '1px solid #e2e5e9', borderRadius: 8,
                  background: '#f5f5f5', color: '#333', fontSize: 13, fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={handleSave}
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '8px 16px', border: 'none', borderRadius: 8,
                  background: '#1976d2', color: '#fff', fontSize: 13, fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {isEdit ? '예약 수정' : '예약 확정'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
